import {
    BadgePercent,
    Banknote,
    BarChart3,
    Building2,
    Globe,
    LayoutDashboard,
    Megaphone,
    MessageSquare,
    Radio,
    Repeat2,
    ShoppingCart,
    Shield,
    Store,
    Truck,
    Users,
    Wallet,
    Warehouse,
    type LucideIcon,
} from "lucide-react";

import type { BusinessModuleId } from "../../routes/moduleNavigation";
import { routePaths } from "../../routes/navigation";

export const drawerWidth = 304;
export const collapsedDrawerWidth = 96;
export const sidebarStorageKey = "app-shell-sidebar";

export const navIcons = {
    [routePaths.adminAccess]: Shield,
    [routePaths.messages]: MessageSquare,
    [routePaths.dashboard]: LayoutDashboard,
    [routePaths.dashboardWorkspace]: LayoutDashboard,
    [routePaths.clients]: Users,
    [routePaths.orders]: ShoppingCart,
    [routePaths.orderItems]: ShoppingCart,
    [routePaths.loyalty]: BadgePercent,
    [routePaths.b2b]: Building2,
    [routePaths.wholesaleSuppliers]: Truck,
    [routePaths.campaigns]: Megaphone,
    [routePaths.bi]: BarChart3,
    [routePaths.retailFrequency]: Repeat2,
    [routePaths.expensesTransport]: Truck,
    [routePaths.expensesMarketing]: Radio,
    [routePaths.expensesSalaries]: Banknote,
} satisfies Record<string, LucideIcon>;

export const moduleNavIcons: Record<BusinessModuleId, LucideIcon> = {
    global: LayoutDashboard,
    retail: Store,
    wholesale: Building2,
    online: Globe,
    warehouse: Warehouse,
    expenses: Wallet,
};

export interface ModuleNavTheme {
    accent: string;
    accentSoft: string;
    accentMuted: string;
    accentBorder: string;
    iconColor: string;
    iconBg: string;
}

/** Цветове на главните модули в страничното меню. */
export const moduleNavThemes: Record<BusinessModuleId, ModuleNavTheme> = {
    global: {
        accent: "#14325c",
        accentSoft: "rgba(20, 50, 92, 0.14)",
        accentMuted: "rgba(20, 50, 92, 0.06)",
        accentBorder: "rgba(20, 50, 92, 0.28)",
        iconColor: "#0f2340",
        iconBg: "rgba(20, 50, 92, 0.16)",
    },
    retail: {
        accent: "#b91c1c",
        accentSoft: "rgba(185, 28, 28, 0.14)",
        accentMuted: "rgba(185, 28, 28, 0.06)",
        accentBorder: "rgba(185, 28, 28, 0.28)",
        iconColor: "#991b1b",
        iconBg: "rgba(185, 28, 28, 0.16)",
    },
    wholesale: {
        accent: "#4f46e5",
        accentSoft: "rgba(79, 70, 229, 0.14)",
        accentMuted: "rgba(79, 70, 229, 0.06)",
        accentBorder: "rgba(79, 70, 229, 0.28)",
        iconColor: "#4338ca",
        iconBg: "rgba(79, 70, 229, 0.16)",
    },
    online: {
        accent: "#0284c7",
        accentSoft: "rgba(2, 132, 199, 0.14)",
        accentMuted: "rgba(2, 132, 199, 0.06)",
        accentBorder: "rgba(2, 132, 199, 0.28)",
        iconColor: "#0369a1",
        iconBg: "rgba(2, 132, 199, 0.16)",
    },
    warehouse: {
        accent: "#c2410c",
        accentSoft: "rgba(194, 65, 12, 0.14)",
        accentMuted: "rgba(194, 65, 12, 0.06)",
        accentBorder: "rgba(194, 65, 12, 0.28)",
        iconColor: "#9a3412",
        iconBg: "rgba(194, 65, 12, 0.16)",
    },
    expenses: {
        accent: "#c45c62",
        accentSoft: "rgba(196, 92, 98, 0.14)",
        accentMuted: "rgba(196, 92, 98, 0.06)",
        accentBorder: "rgba(196, 92, 98, 0.28)",
        iconColor: "#a84a50",
        iconBg: "rgba(196, 92, 98, 0.16)",
    },
};

export function moduleNavIconSx(moduleId: BusinessModuleId) {
    const colors = moduleNavThemes[moduleId];

    return {
        color: colors.iconColor,
        background: colors.iconBg,
    };
}

export function moduleNavHeaderSx(moduleId: BusinessModuleId, isActive: boolean) {
    const colors = moduleNavThemes[moduleId];

    return {
        borderLeft: `3px solid ${colors.accent}`,
        bgcolor: isActive ? colors.accentSoft : colors.accentMuted,
        borderColor: isActive ? colors.accentBorder : "transparent",
        "&.Mui-selected": {
            bgcolor: colors.accentSoft,
            borderColor: colors.accentBorder,
            boxShadow: `0 10px 24px ${colors.accentMuted}`,
        },
        "&:hover": {
            bgcolor: colors.accentSoft,
            borderColor: colors.accentBorder,
        },
        "&.Mui-selected:hover": {
            bgcolor: colors.accentSoft,
        },
    };
}
